import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { Zap, LogOut } from "lucide-react";
import LanguageSelector from "./LanguageSelector";
import DevAccountBadge from "./DevAccountBadge";
import DailyUsageBadge from "./DailyUsageBadge";

export default function Navbar() {
    const { user, tokens, plan, logout } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const isUnlimitedPlan = plan === "studio" || plan === "studio_member";

    const linkClass = ({ isActive }) =>
        `text-sm transition-colors ${isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"}`;

    return (
        <header className="sticky top-0 z-50 glass border-b border-slate-200">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="brand-link">
                    <img src="/logo.png" alt="Vivoz" className="h-9 w-auto" />
                </Link>
                <nav className="hidden md:flex items-center gap-8">
                    {!user && (
                        <>
                            <NavLink to="/#features" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-features">
                                {t("nav.features")}
                            </NavLink>
                            <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing">
                                {t("nav.pricing")}
                            </NavLink>
                        </>
                    )}
                    {user && (
                        <>
                            <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">
                                {t("nav.dashboard")}
                            </NavLink>
                            <NavLink to="/practice" className={linkClass} data-testid="nav-practice">
                                {t("nav.practice")}
                            </NavLink>
                            <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing">
                                {t("nav.pricing")}
                            </NavLink>
                            {(user.plan === "studio" || user.plan === "studio_member") && (
                                <NavLink to="/team" className={linkClass} data-testid="nav-team">
                                    {t("nav.team")}
                                </NavLink>
                            )}
                            <NavLink to="/leaderboard" className={linkClass} data-testid="nav-leaderboard">
                                {t("nav.leaderboard")}
                            </NavLink>
                            <NavLink to="/settings" className={linkClass} data-testid="nav-settings">
                                {t("nav.settings")}
                            </NavLink>
                        </>
                    )}
                </nav>
                <div className="flex items-center gap-3">
                    {user?.is_test_account && <DevAccountBadge />}
                    {user && <DailyUsageBadge />}
                    {user && (
                        <div className="flex items-center gap-2 px-3 py-1.5 border border-brand/40 text-sm" data-testid="nav-tokens">
                            <Zap size={14} className="text-brand" />
                            <span className="font-mono text-slate-900">{isUnlimitedPlan ? "∞" : tokens}</span>
                            <span className="mono-label">{t("nav.tokens")}</span>
                        </div>
                    )}
                    <LanguageSelector />
                    {!user ? (
                        <>
                            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900" data-testid="nav-login">
                                {t("nav.login")}
                            </Link>
                            <Link to="/register" className="btn-primary text-sm" data-testid="nav-signup">
                                {t("nav.signup")}
                            </Link>
                        </>
                    ) : (
                        <button
                            onClick={() => { logout(); nav("/"); }}
                            className="text-sm text-slate-700 hover:text-slate-900 flex items-center gap-1"
                            data-testid="nav-logout"
                        >
                            <LogOut size={14} /> {t("nav.logout")}
                        </button>
                    )}
                </div>
            </div>
        </header>
    );
}
